import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/auth';
import { Tag, Plus, Search, Filter, SortAsc, SortDesc, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem } from '@/components/ui/dropdown-menu';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PromoCodeTable from './promocodes/PromoCodeTable';
import PromoCodeDialog from './promocodes/PromoCodeDialog';
import { usePromoCodesManager } from '@/hooks/sms/usePromoCodesManager';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PromoCode } from '@/hooks/sms/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { UserRole } from '@/context/auth/types';
import { Skeleton } from '@/components/ui/skeleton';

const ITEMS_PER_PAGE = 10;

const PromoCodesTab: React.FC = () => {
  const { user } = useAuth();
  const { selectedRestaurants, activeRestaurantDetails, restaurantDetails } = useRestaurantContext();
  const userRestaurants = restaurantDetails || [];
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [discountTypeFilter, setDiscountTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [view, setView] = useState<'all' | 'org' | 'restaurant'>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<'name' | 'date' | 'usage' | 'code'>('date');
  const [codeToEdit, setCodeToEdit] = useState<PromoCode | null>(null);
  const [codeToDuplicate, setCodeToDuplicate] = useState<PromoCode | null>(null);
  const [codeToDelete, setCodeToDelete] = useState<PromoCode | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const { 
    promoCodes, 
    loading, 
    error,
    isCreating,
    isDeleting,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    refreshPromoCodes
  } = usePromoCodesManager(user, selectedRestaurants);
  
  const canCreatePromoCode = user && (
    user.role === UserRole.SUPER_ADMIN || 
    user.role === UserRole.ORG_ADMIN || 
    user.role === UserRole.REST_ADMIN
  );
  
  const hasPromoCodes = promoCodes && promoCodes.length > 0;
  
  const discountTypes = [...new Set(promoCodes.map(code => code.discount_type))];
  
  const getCodeStatus = (code: PromoCode): string => {
    if (!code.active) return 'inactive';
    if (code.end_date && new Date(code.end_date) < new Date()) return 'expired';
    if (code.usage_limit && code.current_usage >= code.usage_limit) return 'limit_reached';
    if (code.start_date && new Date(code.start_date) > new Date()) return 'scheduled';
    return 'active';
  };
  
  const filteredPromoCodes = promoCodes.filter(code => {
    const matchesSearch = !searchQuery || 
      code.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      code.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDiscountType = discountTypeFilter.length === 0 || 
      discountTypeFilter.includes(code.discount_type);
    
    const codeStatus = getCodeStatus(code);
    const matchesStatus = statusFilter.length === 0 || 
      statusFilter.includes(codeStatus);
    
    let matchesView = true;
    if (view === 'org') {
      matchesView = !code.restaurant_id || code.restaurant_id === 'org';
    } else if (view === 'restaurant') {
      matchesView = !!code.restaurant_id && code.restaurant_id !== 'org';
    }
    
    return matchesSearch && matchesDiscountType && matchesStatus && matchesView;
  }).sort((a, b) => {
    if (sortField === 'name') {
      return sortOrder === 'asc' 
        ? a.name.localeCompare(b.name) 
        : b.name.localeCompare(a.name);
    } else if (sortField === 'code') {
      return sortOrder === 'asc' 
        ? a.code.localeCompare(b.code) 
        : b.code.localeCompare(a.code);
    } else if (sortField === 'usage') {
      return sortOrder === 'asc' ? a.current_usage - b.current_usage : b.current_usage - a.current_usage;
    } else {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }
  });
  
  const totalPages = Math.max(1, Math.ceil(filteredPromoCodes.length / ITEMS_PER_PAGE));
  const paginatedPromoCodes = filteredPromoCodes.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handleDiscountTypeToggle = (type: string) => {
    setDiscountTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };
  
  const handleStatusToggle = (status: string) => {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };
  
  const handleEditPromoCode = (id: number) => {
    const code = promoCodes.find(c => c.id === id);
    if (code) {
      setCodeToEdit(code);
      setIsCreateDialogOpen(true);
    }
  };
  
  const handleDuplicatePromoCode = (id: number) => {
    const code = promoCodes.find(c => c.id === id);
    if (code) {
      setCodeToDuplicate(code);
      setIsCreateDialogOpen(true);
    }
  };
  
  const handleDeletePromoCode = (id: number) => {
    const code = promoCodes.find(c => c.id === id);
    if (code) {
      setCodeToDelete(code);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!codeToDelete) return;

    try {
      await deletePromoCode(codeToDelete.id);
      setIsDeleteDialogOpen(false);
      setCodeToDelete(null);
    } catch (error) {
      console.error('Error deleting promo code:', error);
    }
  };
  
  const handleSort = (field: 'name' | 'date' | 'usage' | 'code') => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleSavePromoCode = async (data: any) => {
    try {
      if (codeToEdit && !codeToDuplicate) {
        // Update existing code
        await updatePromoCode(codeToEdit.id, data);
      } else {
        // Create new code (including duplicates)
        await createPromoCode(data);
      }
      setIsCreateDialogOpen(false);
      setCodeToEdit(null);
      setCodeToDuplicate(null);
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error saving promo code:', error);
    }
  };

  const handleSaveMultiplePromoCodes = async (baseData: any, restaurantIds: string[]) => {
    try {
      let successCount = 0;
      let failureCount = 0;

      for (const restaurantId of restaurantIds) {
        try {
          // Create a copy for each restaurant
          const codeData = {
            ...baseData,
            restaurant_id: restaurantId,
            // Generate unique code for each restaurant if not manually specified
            code: baseData.code + (restaurantIds.length > 1 ? `_${restaurantId.slice(-4)}` : '')
          };
          
          await createPromoCode(codeData);
          successCount++;
        } catch (error) {
          console.error(`Error creating promo code for restaurant ${restaurantId}:`, error);
          failureCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: "Success",
          description: `${successCount} promo code(s) created successfully${failureCount > 0 ? `, ${failureCount} failed` : ''}`
        });
      }

      if (successCount > 0 || failureCount === 0) {
        setIsCreateDialogOpen(false);
        setCodeToEdit(null);
        setCodeToDuplicate(null);
      }
    } catch (error) {
      console.error('Error in batch promo code creation:', error);
      toast({
        title: "Error",
        description: "Failed to create multiple promo codes",
        variant: "destructive"
      });
    }
  };
  
  const showNoRestaurantWarning = selectedRestaurants.length === 0;

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium">Promo Codes</h3>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={!canCreatePromoCode || showNoRestaurantWarning}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          New Promo Code
        </Button>
      </div>
      
      {showNoRestaurantWarning ? (
        <Alert variant="default" className="mb-4 border-amber-500 bg-amber-50 text-amber-900">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertDescription>
            Please select a restaurant to view and manage promo codes
          </AlertDescription>
        </Alert>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      ) : (
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-10 w-64" />
                  <Skeleton className="h-10 w-32" />
                </div>
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </div>
            ) : !hasPromoCodes ? (
              <div className="flex flex-col items-center text-center p-8">
                <Tag className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Create Your First Promo Code</h3>
                <p className="text-muted-foreground mb-4">
                  Promo codes help track campaign effectiveness and calculate ROI.
                </p>
                <Button 
                  onClick={() => setIsCreateDialogOpen(true)}
                  disabled={!canCreatePromoCode}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Create Promo Code
                </Button>
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search promo codes..."
                      className="pl-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Tabs
                      value={view}
                      onValueChange={(v) => setView(v as 'all' | 'org' | 'restaurant')}
                      className="w-auto"
                    >
                      <TabsList className="grid grid-cols-3 w-[300px]">
                        <TabsTrigger value="all">All</TabsTrigger>
                        <TabsTrigger value="org">Organisation</TabsTrigger>
                        <TabsTrigger value="restaurant">Restaurant</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Filter className="h-4 w-4" />
                          {(discountTypeFilter.length + statusFilter.length) > 0 ? (
                            <Badge variant="secondary" className="rounded-sm">
                              {discountTypeFilter.length + statusFilter.length}
                            </Badge>
                          ) : (
                            "Filter"
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>Discount Type</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {discountTypes.map((type) => (
                          <DropdownMenuCheckboxItem
                            key={type}
                            checked={discountTypeFilter.includes(type)}
                            onCheckedChange={() => handleDiscountTypeToggle(type)}
                          >
                            {type === 'percentage_off' ? 'Percentage Off' :
                             type === 'dollar_off' ? 'Dollar Off' : 'Free Item'}
                          </DropdownMenuCheckboxItem>
                        ))}
                        
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Status</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {['active', 'inactive', 'expired', 'limit_reached', 'scheduled'].map((status) => (
                          <DropdownMenuCheckboxItem
                            key={status}
                            checked={statusFilter.includes(status)}
                            onCheckedChange={() => handleStatusToggle(status)}
                          >
                            {status === 'limit_reached' ? 'Limit Reached' :
                             status.charAt(0).toUpperCase() + status.slice(1)}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          {sortOrder === 'asc' ? (
                            <SortAsc className="h-4 w-4" />
                          ) : (
                            <SortDesc className="h-4 w-4" />
                          )}
                          Sort
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Sort Promo Codes</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'code'}
                          onCheckedChange={() => handleSort('code')}
                        >
                          By Code
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'name'}
                          onCheckedChange={() => handleSort('name')}
                        >
                          By Name
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'date'}
                          onCheckedChange={() => handleSort('date')}
                        >
                          By Date
                        </DropdownMenuCheckboxItem>
                        <DropdownMenuCheckboxItem
                          checked={sortField === 'usage'}
                          onCheckedChange={() => handleSort('usage')}
                        >
                          By Usage
                        </DropdownMenuCheckboxItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                <PromoCodeTable
                  promoCodes={paginatedPromoCodes}
                  onEdit={handleEditPromoCode}
                  onDuplicate={handleDuplicatePromoCode}
                  onDelete={handleDeletePromoCode}
                />
                
                {totalPages > 1 && (
                  <Pagination className="mt-4">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                      
                      {Array.from({ length: totalPages }).map((_, i) => {
                        if (
                          i === 0 || 
                          i === totalPages - 1 || 
                          (i >= currentPage - 2 && i <= currentPage + 0)
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink
                                isActive={currentPage === i + 1}
                                onClick={() => handlePageChange(i + 1)}
                              >
                                {i + 1}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (
                          i === currentPage - 3 || 
                          i === currentPage + 1
                        ) {
                          return (
                            <PaginationItem key={i}>
                              <PaginationLink>...</PaginationLink>
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}
                      
                      <PaginationItem>
                        <PaginationNext
                          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
      
      <PromoCodeDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            setCodeToEdit(null);
            setCodeToDuplicate(null);
          }
        }}
        onSave={handleSavePromoCode}
        onSaveMultiple={handleSaveMultiplePromoCodes}
        codeToEdit={codeToEdit || codeToDuplicate}
        restaurantId={activeRestaurantDetails?.id}
        organisationId={activeRestaurantDetails?.organisationId}
        restaurants={userRestaurants}
        isLoading={isCreating || isDeleting}
        isDuplicating={!!codeToDuplicate}
      />
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Promo Code</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the promo code "{codeToDelete?.code}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setCodeToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete Promo Code'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PromoCodesTab;